import { RowActions, StatusChip, type ChipTone, MONO_LABEL_TYPE } from '@/components/editorial';
import {
  AlertTriangle,
  CalendarDays,
  Check,
  ChevronRight,
  Coffee,
  Pill,
  Stethoscope,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { COARSE_HIT_ALONE, COARSE_HIT_BEHIND } from '@/lib/touch-target';
import { Card } from '@/components/ui/card';
import { safeFormatIntl, dateOnlyIntl } from '@/lib/safe-date';
import { labelFor } from '@/i18n/labels';
import { useWeekdayDate } from './useWeekdayDate';
import {
  MED_FREQUENCY_LABEL_KEY,
  QUESTION_CATEGORY_LABEL_KEY,
  QUESTION_PRIORITY_LABEL_KEY,
  SYMPTOM_SEVERITY_LABEL_KEY,
  SYMPTOM_SEVERITY_TOKENS,
  SYMPTOM_TYPE_LABEL_KEY,
  isRefillSoon,
  reactionsForMedication,
} from '@/lib/medical-display';
import type {
  DoctorQuestion,
  MedicalAppointment,
  Medication,
  SymptomEntry,
} from '@/types/api';

// ── Row actions ───────────────────────────────────────────────────────────
//
// `RowActions` is an editorial primitive since 2026-09-10; re-exported here
// for the importers that learned it from this file.
export { RowActions };

/**
 * One fact on the medication row. `kind` picks the rendering — the with-food
 * fact carries its icon — so nothing compares a rendered string to decide
 * (R3-B22: the old check matched the English "with food").
 */
export interface MedFact {
  kind: 'text' | 'withFood';
  label: string;
}


// ── Medication row ────────────────────────────────────────────────────────
//
// A roster row, not a schedule card (2026-09-09). The four-slot MORNING /
// MIDDAY / EVENING / BEDTIME grid and the "Started recently" footer were the
// Apple Health half of the old card; what TRF needs from this list is
// CONTEXT — what the child is on, so reactions can link to it, Maya knows the
// regimen and the printed summary carries it. The time-of-day field stays in
// the edit form because the Today card's dose rows still read it.

interface MedicationCardProps {
  med: Medication;
  symptoms: SymptomEntry[];
  onLogReaction?: (med: Medication) => void;
  onOpen?: (med: Medication) => void;
  onEdit?: (med: Medication) => void;
  onDelete?: (med: Medication) => void;
}

export function MedicationCard({ med, symptoms, onLogReaction, onOpen, onEdit, onDelete }: MedicationCardProps) {
  const { t } = useTranslation(['health', 'common']);
  const weekdayDate = useWeekdayDate();
  const refillSoon = isRefillSoon(med);
  const reactionCount = reactionsForMedication(symptoms, med._id);

  const candidates: (MedFact | null)[] = [
    med.purpose ? { kind: 'text', label: med.purpose } : null,
    { kind: 'text', label: labelFor(MED_FREQUENCY_LABEL_KEY, med.frequency, t) },
    med.with_food ? { kind: 'withFood', label: t('health:card.withFood') } : null,
    med.prescribing_doctor ? { kind: 'text', label: t('health:card.prescribedBy', { doctor: med.prescribing_doctor }) } : null,
  ];
  const facts = candidates.filter((f): f is MedFact => f !== null && f.label !== '');

  return (
    <Card asChild className="shadow-none">
    <article className={cn('px-4 py-3', !med.active && 'opacity-70')}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            {/* The medication name clicks through to the detail view (D-F),
                where the dose log and the archive/reactivate toggle live. */}
            <h3 className="font-serif text-base font-medium text-foreground">
              {onOpen ? (
                <button
                  type="button"
                  onClick={() => onOpen(med)}
                  // #2661: the name is ~24px tall on a phone. Its hit area
                  // reaches 10px above and below only (44px) and never
                  // sideways. Standalone COARSE_HIT_ALONE, not the -z-10 flavour: the
                  // facts line below is an in-flow box that would swallow a
                  // hit area painted behind it, and it holds no control.
                  className={cn(
                    COARSE_HIT_ALONE,
                    '[@media(pointer:coarse)]:after:-inset-y-2.5 [@media(pointer:coarse)]:after:inset-x-0',
                    'text-left hover:underline underline-offset-2 transition-colors',
                  )}
                >
                  {med.medication_name}
                </button>
              ) : (
                med.medication_name
              )}
            </h3>
            {med.generic_name && med.generic_name !== med.medication_name && (
              <span className="text-caption italic text-stevens-ink-3">({med.generic_name})</span>
            )}
            <span className="rounded-md border border-border px-2 py-0.5 text-label font-mono text-foreground">
              {med.dose}
            </span>
          </div>

          <p className="mt-1 text-caption text-stevens-ink-3">
            {facts.map((f, i) => (
              <span key={`${f.kind}-${f.label}`}>
                {i > 0 && <span aria-hidden className="mx-1.5 text-border">·</span>}
                {f.kind === 'withFood' ? (
                  <span className="inline-flex items-center gap-1"><Coffee className="h-3 w-3" /> {f.label}</span>
                ) : f.label}
              </span>
            ))}
          </p>

          {/* Each note is its own line — as inline-flex siblings the refill
              chip and the reactions count ran into each other (prod, 2026-09-09). */}
          {med.active && med.refill_reminder && med.refill_date && refillSoon && (
            <p className="mt-1.5 flex w-fit items-center gap-1.5 rounded px-2 py-0.5 -mx-2 text-caption bg-status-warn text-status-warn-fg">
              <Pill className="h-3.5 w-3.5" />
              {t('health:card.refillAvailable', { date: weekdayDate(med.refill_date, { dateOnly: true }) })}
            </p>
          )}
          {reactionCount > 0 && (
            <p className="mt-1 flex w-fit items-center gap-1.5 text-caption text-status-danger-fg">
              <AlertTriangle className="h-3.5 w-3.5" />
              {t('health:card.reactionsLogged', { count: reactionCount })}
            </p>
          )}
          {!med.active && med.end_date && (
            <p className="mt-1 flex w-fit items-center gap-1.5 text-caption text-stevens-ink-3">
              <CalendarDays className="h-3.5 w-3.5" />
              {t('health:card.ended', { date: dateOnlyIntl(med.end_date, 'monthDayYear') })}
            </p>
          )}
        </div>

        {/* #2420 follow-up: Log reaction sits 10px from RowActions' touch hit
            area on a coarse pointer; desktop keeps gap-2. The text button is
            ~17px tall and already wide, so on a coarse pointer its ::after
            reaches 14px above and below only (44px), at -z-10 under this
            `isolate` cluster: it never reaches sideways into the pencil's
            hit area, and RowActions' own stacking context paints above it. */}
        <div className="isolate flex items-center gap-2 [@media(pointer:coarse)]:gap-2.5 flex-shrink-0">
          {med.active && onLogReaction && (
            <button
              type="button"
              onClick={() => onLogReaction(med)}
              className={cn(
                COARSE_HIT_BEHIND,
                '[@media(pointer:coarse)]:after:-inset-y-3.5 [@media(pointer:coarse)]:after:inset-x-0',
                'text-label text-stevens-ink-3 hover:text-foreground transition-colors',
              )}
            >
              {t('health:card.logReaction')}
            </button>
          )}
          <RowActions
            editLabel={t('health:card.editMedication')}
            deleteLabel={t('health:card.deleteMedication')}
            onEdit={onEdit ? () => onEdit(med) : undefined}
            onDelete={onDelete ? () => onDelete(med) : undefined}
          />
        </div>
      </div>
    </article>
    </Card>
  );
}

/**
 * #2420 (D-2420 option A): the question footer's 11 px text actions (~17 px
 * tall) get a 44 px hit area on a coarse pointer — 14 px above and below —
 * with nothing drawn differently. The ::after sits at -z-10 inside the card's
 * `isolate` context, beneath the question text it reaches toward, so a tap on
 * the question still opens it. Desktop renders pixel-identical.
 */
const FOOTER_HIT = `${COARSE_HIT_BEHIND} [@media(pointer:coarse)]:after:-inset-y-3.5 [@media(pointer:coarse)]:after:-inset-x-1.5`;

// ── Question card ─────────────────────────────────────────────────────────

interface QuestionCardProps {
  q: DoctorQuestion;
  linkedAppt?: MedicalAppointment;
  /** Opens the detail view (edit/delete live there) — the question text
   *  itself is the click target (D-F). */
  onOpen?: (q: DoctorQuestion) => void;
  /** Opens the doctor's-answer capture flow. Labeled "Answer" on the card —
   *  the old label was "Edit", which it never was (#847). */
  onAnswer?: (q: DoctorQuestion) => void;
  onMoveToVisit?: (q: DoctorQuestion) => void;
  onAddFollowUp?: (q: DoctorQuestion) => void;
  onEdit?: (q: DoctorQuestion) => void;
  onDelete?: (q: DoctorQuestion) => void;
}

// Question priority is urgency, not an error state (#762): `this_visit` wore
// danger-rose on the family's own medical card — §3's tone-failure list says
// a medical surface never cries wolf. It is warn now; answered mutes to
// neutral at the call site.
const PRIORITY_TONE: Record<DoctorQuestion['priority'], ChipTone> = {
  this_visit:    'warn',
  can_wait:      'neutral',
  already_asked: 'ok',
};

export function QuestionCard({ q, linkedAppt, onOpen, onAnswer, onMoveToVisit, onAddFollowUp, onEdit, onDelete }: QuestionCardProps) {
  // Once answered, the priority pill stops being a live signal — mute it so
  // the green "Answered" chip becomes the dominant marker on the card.
  const { t } = useTranslation(['health', 'common']);
  const weekdayDate = useWeekdayDate();
  const priorityTone = q.answered ? 'neutral' : PRIORITY_TONE[q.priority];
  return (
    <Card asChild className="shadow-none">
    {/* `isolate` keeps the footer's touch hit areas (#2420) beneath the drawn card. */}
    <article className={cn('isolate px-4 py-3.5', q.answered && 'opacity-90')}>
      {/* #2420 follow-up: the question button below sits outside RowActions'
          10px touch hit area on a coarse pointer. */}
      <div className="flex items-center gap-2 mb-2 [@media(pointer:coarse)]:mb-3 flex-wrap">
        <StatusChip tone={priorityTone}>
          {labelFor(QUESTION_PRIORITY_LABEL_KEY, q.priority, t)}
        </StatusChip>
        <span className="text-label text-stevens-ink-3">
          {labelFor(QUESTION_CATEGORY_LABEL_KEY, q.category, t)}
        </span>
        {q.answered ? (
          <span className="ml-auto inline-flex items-center gap-1 text-label text-status-ok-fg">
            <Check className="h-3 w-3" /> {t('health:card.answered')}
          </span>
        ) : linkedAppt ? (
          <span className="ml-auto inline-flex items-center gap-1 text-label text-stevens-ink-3">
            <CalendarDays className="h-3 w-3" /> {t('health:card.forDate', { date: weekdayDate(linkedAppt.date_time) })}
          </span>
        ) : null}
        <RowActions
          editLabel={t('health:card.editQuestion')}
          deleteLabel={t('health:card.deleteQuestion')}
          className={q.answered || linkedAppt ? '' : 'ml-auto'}
          onEdit={onEdit ? () => onEdit(q) : undefined}
          onDelete={onDelete ? () => onDelete(q) : undefined}
        />
      </div>

      {onOpen ? (
        <p className="font-serif text-lede leading-snug text-foreground">
          <button
            type="button"
            onClick={() => onOpen(q)}
            className="text-left hover:underline underline-offset-2 transition-colors"
          >
            {q.question}
          </button>
        </p>
      ) : (
        <p className="font-serif text-lede leading-snug text-foreground">
          {q.question}
        </p>
      )}

      {q.answered && q.answer_notes && (
        <div className="mt-3 rounded-md border border-border/60 bg-muted/40 px-3 py-2">
          <div className="text-label text-stevens-ink-3 inline-flex items-center gap-1 mb-1">
            <Stethoscope className="h-3 w-3" />
            {t('health:card.answerRecorded')}
          </div>
          <p
            className="font-serif text-body italic text-foreground/85 leading-snug"
          >
            {t('health:quoted', { text: q.answer_notes })}
          </p>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-label text-stevens-ink-3">
        <span>{safeFormatIntl(q.created_at, 'monthDay')}</span>
        <div className="flex items-center gap-3">
          {!q.answered && onAnswer && (
            <button type="button" onClick={() => onAnswer(q)} className={cn(FOOTER_HIT, 'hover:text-foreground transition-colors')}>
              {t('health:card.answer')}
            </button>
          )}
          {!q.answered && q.priority !== 'this_visit' && onMoveToVisit && (
            <button type="button" onClick={() => onMoveToVisit(q)} className={cn(FOOTER_HIT, 'hover:text-foreground transition-colors')}>
              {t('health:card.moveToNextVisit')}
            </button>
          )}
          {q.answered && onAddFollowUp && (
            <button type="button" onClick={() => onAddFollowUp(q)} className={cn(FOOTER_HIT, 'hover:text-foreground transition-colors')}>
              {t('health:card.addFollowUp')}
            </button>
          )}
          {onOpen && (
            <button
              type="button"
              onClick={() => onOpen(q)}
              className={cn(FOOTER_HIT, 'inline-flex items-center gap-1 hover:text-foreground transition-colors')}
            >
              {t('common:actions.details')} <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </article>
    </Card>
  );
}

// ── Symptom entry (timeline) ──────────────────────────────────────────────

interface SymptomEntryRowProps {
  s: SymptomEntry;
  linkedMed?: Medication;
  /** Opens the entry's detail view. The description text is the click target (D-F). */
  onOpen?: (s: SymptomEntry) => void;
  onEdit?: (s: SymptomEntry) => void;
  onDelete?: (s: SymptomEntry) => void;
}

export function SymptomEntryRow({ s, linkedMed, onOpen, onEdit, onDelete }: SymptomEntryRowProps) {
  const { t } = useTranslation(['health', 'common']);
  const tokens = SYMPTOM_SEVERITY_TOKENS[s.severity];

  return (
    <div className="grid grid-cols-[88px_24px_1fr] gap-3 items-start">
      <div className="flex flex-col items-end pt-0.5 text-right">
        {/* `s.date` is date-only — the dialog offers no time field (#1308).
            The stamp under it used to render that day's UTC midnight in the
            reader's zone, which both shifted the day and invented a clock
            time ("Aug 26 · 8:00 PM" for an entry logged on the 27th). */}
        <span className={cn(MONO_LABEL_TYPE, 'text-stevens-ink-3')}>
          {dateOnlyIntl(s.date, 'monthDay')}
        </span>
      </div>

      <div className="flex flex-col items-center self-stretch">
        <span className={cn('mt-1.5 w-3 h-3 rounded-full', tokens.marker)} />
        <span className={cn('flex-1 w-px mt-1 mb-1', tokens.rail)} />
      </div>

      <Card className="px-3.5 py-3 mb-3 shadow-none">
        {/* #2420 follow-up: the entry button below sits outside RowActions'
            10px touch hit area on a coarse pointer. */}
        <div className="flex items-center gap-2 flex-wrap mb-1.5 [@media(pointer:coarse)]:mb-3">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-label font-medium uppercase',
              tokens.pill,
            )}
          >
            <span className={cn('w-[6px] h-[6px] rounded-full', tokens.marker)} />
            {labelFor(SYMPTOM_SEVERITY_LABEL_KEY, s.severity, t)}
          </span>
          <span className="text-label text-stevens-ink-3">
            {labelFor(SYMPTOM_TYPE_LABEL_KEY, s.type, t)}
          </span>
          {linkedMed && (
            <StatusChip tone="violet">
              <Pill className="h-3 w-3" />
              {linkedMed.medication_name}
            </StatusChip>
          )}
          {s.reported_to_doctor && (
            <StatusChip tone="ok">
              <Check className="h-3 w-3" /> {t('health:card.reported')}
            </StatusChip>
          )}
          <RowActions
            editLabel={t('health:card.editEntry')}
            deleteLabel={t('health:card.deleteEntry')}
            className="ml-auto"
            onEdit={onEdit ? () => onEdit(s) : undefined}
            onDelete={onDelete ? () => onDelete(s) : undefined}
          />
        </div>

        {onOpen ? (
          <p className="text-body text-foreground/90 leading-relaxed">
            <button
              type="button"
              onClick={() => onOpen(s)}
              className="text-left hover:underline underline-offset-2 transition-colors"
            >
              {s.description}
            </button>
          </p>
        ) : (
          <p className="text-body text-foreground/90 leading-relaxed">{s.description}</p>
        )}

        {s.what_helped && (
          <p className="font-serif mt-1.5 text-caption italic text-stevens-ink-3">
            <span className={cn(MONO_LABEL_TYPE, 'not-italic text-stevens-ink-3/70 mr-1')}>
              {t('health:card.helped')}
            </span>
            {s.what_helped}
          </p>
        )}

        {s.doctor_response && (
          <div className="mt-2.5 rounded-md border border-border/60 bg-muted/40 px-3 py-2">
            <div className="text-label text-stevens-ink-3 inline-flex items-center gap-1 mb-1">
              <Stethoscope className="h-3 w-3" /> {t('health:card.doctorResponded')}
            </div>
            <p
              className="font-serif text-body italic text-foreground/85 leading-snug"
            >
              {t('health:quoted', { text: s.doctor_response })}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
